// SPDX-License-Identifier: Apache-2.0

// Permission modal for tool calls that need approval (Bash, write tools).
// Traps focus on Allow / Deny while open. Wires the decision back via the
// `onDecide` callback — caller is responsible for sending the WS message back
// to the MCP server.

import { createEffect, onCleanup, Show } from "solid-js";

export interface PermissionRequest {
  id: string;
  toolName: string;
  /** Pretty-printed args summary (truncated by caller). */
  argsSummary: string;
}

interface PermissionModalProps {
  request: PermissionRequest | null;
  onDecide: (id: string, decision: "allow" | "deny") => void;
}

export default function PermissionModal(props: PermissionModalProps) {
  let allowBtn: HTMLButtonElement | undefined;
  let denyBtn: HTMLButtonElement | undefined;

  createEffect(() => {
    const req = props.request;
    if (!req) return;
    // Focus Allow on open.
    queueMicrotask(() => allowBtn?.focus());

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        props.onDecide(req.id, "deny");
        return;
      }
      if (e.key === "Tab") {
        // Trap: only two buttons, ping-pong.
        e.preventDefault();
        const onAllow = document.activeElement === allowBtn;
        (onAllow ? denyBtn : allowBtn)?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });

  return (
    <Show when={props.request}>
      {(req) => (
        <div class="mv-perm__backdrop" role="presentation">
          <div
            class="mv-perm__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mv-perm-title"
          >
            <div class="mv-perm__eyebrow">tool permission</div>
            <h3 id="mv-perm-title" class="mv-perm__title">
              The brain wants to:
            </h3>
            <div class="mv-perm__call">
              <span class="mv-perm__tool">{req().toolName}</span>
              <span class="mv-perm__paren">(</span>
              <span class="mv-perm__args">{req().argsSummary}</span>
              <span class="mv-perm__paren">)</span>
            </div>
            <div class="mv-perm__row">
              <button
                ref={denyBtn}
                type="button"
                class="mv-perm__btn mv-perm__btn--deny"
                onClick={() => props.onDecide(req().id, "deny")}
              >
                Deny
              </button>
              <button
                ref={allowBtn}
                type="button"
                class="mv-perm__btn mv-perm__btn--allow"
                onClick={() => props.onDecide(req().id, "allow")}
              >
                Allow
              </button>
            </div>
          </div>
        </div>
      )}
    </Show>
  );
}
